import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Network,
  RefreshCcw,
  AlertTriangle,
  FileSpreadsheet,
  List,
  PlusCircle,
} from "lucide-react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  ResizableSidebar,
  SearchableSelect,
  SelectField,
} from "@/components/common";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useOrganizationStore } from "../hooks";
import OrganizationTree from "../components/OrganizationTree";
import OrganizationDetails from "../components/OrganizationDetails";
import OrganizationUnitDialog from "../components/OrganizationUnitDialog";
import OrganizationDiagram from "../components/OrganizationDiagram";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import { Label } from "@/components/ui/label";
import OrganizationExcelDialog from "../components/OrganizationExcelDialog";
import { collectDescendantUnits } from "../utils/organization.helpers";

const LEVEL_REGEX = /^level_(\d+)$/;

const resolveGroupBy = (mode) => {
  if (mode === "businessType") return "businessType";
  if (mode === "country") return "country";
  return undefined;
};

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const normalizeKey = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isUnassignedCountryGroup = (node) => {
  if (!node) return false;
  const normalizedKeys = [node.groupKey, node.name, node.id].map(normalizeKey);
  return normalizedKeys.includes("unassigned");
};

const filterCountryTree = (tree) => {
  if (!Array.isArray(tree)) return [];
  return tree.map((root) => {
    if (!Array.isArray(root?.children)) return root;
    return {
      ...root,
      children: root.children.filter(
        (child) => !isUnassignedCountryGroup(child),
      ),
    };
  });
};

const hasMatchFlag = (node) => {
  if (!node) return false;
  if (typeof node.matchesGroup === "boolean") return true;
  return Array.isArray(node.children)
    ? node.children.some((child) => hasMatchFlag(child))
    : false;
};

const matchesGroupFallback = (node, groupMode, groupKey) => {
  if (!node || node.isGroup) return false;
  const normalizedKey = normalizeKey(groupKey);

  if (groupMode === "country") {
    if (normalizedKey === "unassigned") return !node.country;
    return normalizeKey(node.country) === normalizedKey;
  }

  return false;
};

const countGroupMatches = (groupNode, groupMode) => {
  if (!groupNode) return 0;
  const groupKey = groupNode.groupKey;
  const useMatches = hasMatchFlag(groupNode);
  let count = 0;

  const visit = (node) => {
    if (!node) return;

    if (!node.isGroup) {
      if (useMatches) {
        if (node.matchesGroup) count += 1;
      } else if (matchesGroupFallback(node, groupMode, groupKey)) {
        count += 1;
      }
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(visit);
    }
  };

  if (Array.isArray(groupNode.children)) {
    groupNode.children.forEach(visit);
  }

  return count;
};

const OrganizationPage = observer(() => {
  const { t } = useTran();
  const organizationStore = useOrganizationStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [dialogInitial, setDialogInitial] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const [groupMode, setGroupMode] = useState("level_1");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [excelOpen, setExcelOpen] = useState(false);

  const sourceTree =
    groupMode === "businessType"
      ? organizationStore.businessTypeTree
      : groupMode === "country"
        ? organizationStore.countryTree
        : organizationStore.tree;

  const resolvedTree = useMemo(() => {
    if (groupMode === "country") {
      return filterCountryTree(sourceTree);
    }

    if (groupMode === "level_1") {
      return sourceTree;
    }

    if (groupMode === "level_2" || groupMode === "level_3") {
      const filterByLevel = (nodes) =>
        nodes
          ?.map((node) => {
            const children = filterByLevel(node.children || []);

            if (node.unitLevel === groupMode) {
              return { ...node };
            }
            if (children.length > 0) {
              return { ...node, children };
            }

            return null;
          })
          .filter(Boolean);

      return filterByLevel(sourceTree);
    }

    return sourceTree;
  }, [groupMode, sourceTree]);

  const nameOptions = useMemo(() => {
    if (!Array.isArray(resolvedTree)) return [];
    const result = new Map();

    const traverse = (nodes) => {
      if (!Array.isArray(nodes)) return;

      nodes.forEach((node) => {
        if (groupMode === "country") {
          if (node.country && node.countryLabel) {
            result.set(node.country, node.countryLabel);
          }
        } else if (groupMode.startsWith("level_")) {
          if (node.unitLevel === groupMode) {
            const key = node.name.trim();
            if (!result.has(key)) {
              result.set(key, node.name);
            }
          }
        }

        if (Array.isArray(node.children)) {
          traverse(node.children);
        }
      });
    };

    traverse(resolvedTree);
    return Array.from(result, ([value, label]) => ({ value, label }));
  }, [resolvedTree, groupMode, organizationStore.items]);

  const groupCounts = useMemo(() => {
    if (groupMode.startsWith("level_")) {
      const counts = new Map();
      organizationStore.items.forEach((unit) => {
        const level = getLevelNumber(unit.unitLevel);
        if (!level) return;
        counts.set(level, (counts.get(level) || 0) + 1);
      });

      return Array.from(counts.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([level, count]) => ({
          key: `level_${level}`,
          label: `${t("Level")} ${level}`,
          count,
        }));
    }

    const root = resolvedTree?.[0];
    const groups = Array.isArray(root?.children) ? root.children : [];
    return groups.map((group) => ({
      key: group.id || group.groupKey || group.name,
      label: group.name,
      count: countGroupMatches(group, groupMode),
    }));
  }, [groupMode, organizationStore.items, resolvedTree, t]);

  useEffect(() => {
    organizationStore.fetchLookups().catch(() => {});
  }, [organizationStore]);

  useEffect(() => {
    const groupBy = resolveGroupBy(groupMode);
    organizationStore
      .fetchHierarchy({
        groupBy,
        selectId: organizationStore.selectedId,
      })
      .catch(() => {});
  }, [organizationStore, groupMode]);

  const filteredTree = useMemo(() => {
    if (!selectedFilter) return resolvedTree;

    const normalizedFilter = selectedFilter.toLowerCase().trim();

    const filterRecursively = (nodes = []) =>
      nodes
        .map((node) => {
          let matches = false;
          if (groupMode === "country") {
            matches = node.groupKey === normalizedFilter;
          } else if (groupMode.startsWith("level_")) {
            const isCorrectLevel = node.unitLevel === groupMode;
            const isCorrectName =
              node.name?.toLowerCase().trim() === normalizedFilter;

            matches = isCorrectLevel && isCorrectName;
          }

          if (matches) {
            return { ...node };
          }

          const children = filterRecursively(node.children || []);

          if (children.length > 0) {
            return { ...node, children };
          }

          return null;
        })
        .filter(Boolean);

    return filterRecursively(resolvedTree);
  }, [resolvedTree, selectedFilter, groupMode]);

  useEffect(() => {
    setSelectedFilter("");
  }, [groupMode]);

  const handleCreate = (typeHint) => {
    setDialogMode("create");
    setDialogInitial({
      unitLevel: typeHint || "level_1",
      parentId: "",
      region: "",
    });
    setDialogOpen(true);
  };

  const handleEdit = (unit) => {
    if (!unit) return;
    setDialogMode("edit");
    setDialogInitial({
      ...unit,
      parentId: unit.parentId || "",
      region: unit.region || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = (unit) => {
    setPendingDelete(unit);
    setConfirmOpen(true);
  };

  const handleToggleStatus = (unit) => {
    setPendingToggle(unit);
    setToggleConfirmOpen(true);
  };

  const handleDialogSubmit = async (payload) => {
    const groupBy = resolveGroupBy(groupMode);
    if (dialogMode === "create") {
      await organizationStore.createUnit(payload, { groupBy });
    } else {
      await organizationStore.updateUnit(dialogInitial.id, payload, {
        groupBy,
      });
    }
    setDialogOpen(false);
  };

  const confirmDeleteUnit = async () => {
    if (!pendingDelete) return;
    const groupBy = resolveGroupBy(groupMode);
    setConfirmOpen(false);
    await organizationStore.deleteUnit(pendingDelete.id, { groupBy });
    setPendingDelete(null);
  };

  const confirmToggleStatus = async () => {
    if (!pendingToggle) return;
    const groupBy = resolveGroupBy(groupMode);
    const nextStatus =
      pendingToggle.status === "inactive" ? "active" : "inactive";
    setToggleConfirmOpen(false);
    await organizationStore.toggleStatus(pendingToggle.id, nextStatus, {
      groupBy,
    });
    setPendingToggle(null);
  };

  const handleRefresh = () => {
    setGroupMode("level_1");
    setSelectedFilter("");
    const groupBy = resolveGroupBy(groupMode);
    organizationStore
      .fetchHierarchy({
        selectId: organizationStore.selectedId,
        groupBy,
      })
      .catch(() => {});
  };
  const groupOptions = [
    { value: "level_1", label: t("By Business Unit") },
    { value: "level_2", label: t("By Company") },
    { value: "level_3", label: t("By Site") },
    { value: "country", label: t("By Country") },
  ];
  const toggleDescendantCount =
    pendingToggle && pendingToggle.status !== "inactive"
      ? collectDescendantUnits(organizationStore.items, pendingToggle.id).length
      : 0;
  const groupSummary =
    groupCounts.length > 0 ? (
      <div className="flex flex-wrap justify-end gap-2">
        {groupCounts.map((item) => (
          <Badge
            key={item.key}
            variant="secondary"
            className="rounded-full px-2 py-0.5 text-[11px]"
          >
            {item.label}: {item.count}
          </Badge>
        ))}
      </div>
    ) : null;

  const groupToggle = (
    <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-[240px]">
      {/* Group Row */}
      <div className="flex flex-col  sm:flex-row sm:items-center ">
        <Label htmlFor="group-filter" className="text-xs sm:w-10 sm:text-right">
          {t("Filter") + ":"}
        </Label>
        <div className="w-full sm:w-[200px]">
          <SelectField
            options={groupOptions}
            value={groupMode}
            onValueChange={setGroupMode}
            placeholder={t("Select an option")}
            className="w-full"
            selectClassName="w-full rounded-2xl border border-border bg-card"
          />
        </div>
      </div>

      {/* Name Row */}
      <div className="flex flex-col  sm:flex-row sm:items-center ">
        <Label htmlFor="name-filter" className="text-xs sm:w-10 sm:text-right">
          {t("Name") + ":"}
        </Label>
        <div className="w-full sm:w-[200px]">
          <SearchableSelect
            options={nameOptions}
            value={selectedFilter}
            onChange={setSelectedFilter}
            className="w-full flex-shrink-0 overflow-hidden rounded-2xl border border-border bg-card "
          />
        </div>
      </div>

      {viewMode === "diagram"}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Organization Structure")}
        description={t(
          "Model your parent company and multi-level organization structure.",
        )}
        icon={Network}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Can
              resource="organization"
              action="view"
              userPermissions={permissions}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={organizationStore.loading}
              >
                <RefreshCcw
                  className={`${organizationStore.loading ? "animate-spin" : ""}`}
                />
                {t("Refresh")}
              </Button>
              <div className="flex items-center rounded-2xl border border-border bg-card">
                <Button
                  type="button"
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  className="rounded-2xl"
                  onClick={() => setViewMode("list")}
                >
                  <List size={14} />
                  {t("List")}
                </Button>

                <Button
                  type="button"
                  variant={viewMode === "diagram" ? "default" : "ghost"}
                  size="sm"
                  className="rounded-2xl"
                  onClick={() => setViewMode("diagram")}
                >
                  <Network size={14} />
                  {t("Diagram")}
                </Button>
              </div>
            </Can>

            <Can
              resource="organization"
              action="create"
              userPermissions={permissions}
            >
              {/*<Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExcelOpen(true)}
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t("Import / Download")}
              </Button>*/}

              <Button size="sm" type="button" onClick={() => handleCreate()}>
                <PlusCircle />
                {t("Add Unit")}
              </Button>
            </Can>
          </div>
        }
      />

      {organizationStore.error && !dialogOpen ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle size={18} />
          <span>{organizationStore.error}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-xs"
            onClick={() => organizationStore.resetError()}
          >
            {t("Dismiss")}
          </Button>
        </div>
      ) : null}
      {organizationStore.lookupError ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <span>{organizationStore.lookupError}</span>
        </div>
      ) : null}

      {viewMode === "list" ? (
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
          <Can
            resource="organization"
            action="view"
            userPermissions={permissions}
            showMessage={true}
          >
            <ResizableSidebar
              storageKey="nxstart_org_tree_width"
              handleClassName="lg:hidden xl:block"
              handleTitle={t("Drag to resize")}
            >
              <OrganizationTree
                tree={filteredTree}
                headerActions={
                  <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-end">
                    {groupToggle}
                  </div>
                }
              />
            </ResizableSidebar>
            <div className="flex-1">
              <OrganizationDetails
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
              />
            </div>
          </Can>
        </div>
      ) : (
        <div className="space-y-3 rounded-radius border border-border bg-background p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("Relationship Diagram")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  "Visualize how parent companies and organization levels connect.",
                )}
              </p>
            </div>
            <div className="flex w-full justify-end md:w-auto">
              {groupToggle}
            </div>
          </div>

          <OrganizationDiagram tree={filteredTree} groupMode={groupMode} />
        </div>
      )}

      <OrganizationUnitDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogInitial}
        onSubmit={handleDialogSubmit}
        onOpenChange={setDialogOpen}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Delete organization unit")}
        description={t(
          "This action is permanent. Child units must be reassigned or removed before deleting.",
        )}
        tone="danger"
        confirmLabel={
          organizationStore.deletingId === pendingDelete?.id
            ? t("Deleting...")
            : t("Delete")
        }
        cancelLabel={t("Cancel")}
        loading={organizationStore.deletingId === pendingDelete?.id}
        onConfirm={confirmDeleteUnit}
      />
      <ConfirmDialog
        open={toggleConfirmOpen}
        onOpenChange={setToggleConfirmOpen}
        tone={pendingToggle?.status === "inactive" ? "success" : "warning"}
        title={
          pendingToggle?.status === "inactive"
            ? t("Activate organization unit")
            : t("Deactivate organization unit")
        }
        description={
          pendingToggle?.status === "inactive"
            ? t(
                "This unit will become Active again. Child units stay Inactive until activated individually.",
              )
            : toggleDescendantCount > 0
              ? t(
                  "This unit and {{count}} child unit(s) will be marked Inactive. They stay visible, past data still counts in reports, and you can still enter historical data. You can reactivate anytime.",
                  { count: toggleDescendantCount },
                )
              : t(
                  "This unit will be marked Inactive. It stays visible, past data still counts in reports, and you can still enter historical data. You can reactivate anytime.",
                )
        }
        confirmLabel={
          organizationStore.togglingId === pendingToggle?.id
            ? t("Saving...")
            : pendingToggle?.status === "inactive"
              ? t("Activate")
              : t("Deactivate")
        }
        cancelLabel={t("Cancel")}
        loading={organizationStore.togglingId === pendingToggle?.id}
        onConfirm={confirmToggleStatus}
      />
      <OrganizationExcelDialog
        open={excelOpen}
        onOpenChange={setExcelOpen}
        downloading={organizationStore.downloadingTemplate}
        importing={organizationStore.importing}
        requireCompany={false}
        getCompanyId={() => undefined}
        onDownloadTemplate={({ level }) =>
          organizationStore.downloadImportTemplate({ level })
        }
        onImport={(file) =>
          organizationStore.importUnitsExcel(file, {
            groupBy: resolveGroupBy(groupMode),
          })
        }
      />
    </div>
  );
});

export default OrganizationPage;
